import PdfPreview from "../../ui/PdfPreview"
import { useState } from "react"
import { countries, isoCountryMap } from "../../../utils/countryData"
import FileUploadBox from "../../ui/FileUploadBox"
import InfoBox from "../../ui/InfoBox"
import { uploadDocument } from "../../../services/documentService"

export default function NewSeniorityForm({
  onCancel,
  onSubmit,
  initialData,
}: {
  onCancel: () => void
  onSubmit: (data: {
    type: string
    countryCode: string
    applicationNumber: string
    applicationDate: string
    registrationNumber: string
    registrationDate: string
    priorityDate?: string
    euipoDocumentId?: string | null
    originalFileName?: string | null
  }) => void
  initialData?: {
    type?: string
    countryCode?: string
    applicationNumber?: string
    applicationDate?: string | Date | null
    registrationNumber?: string
    registrationDate?: string | Date | null
    priorityDate?: string | Date | null
    euipoDocumentId?: string | null
    originalFileName?: string | null
  } | null
}) {
  const toDateInputValue = (value?: string | Date | null) => {
    if (!value) return ""
    const date = value instanceof Date ? value : new Date(value)
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10)
  }

  const initialCountryCode = Object.prototype.hasOwnProperty.call(
    isoCountryMap,
    initialData?.countryCode || "",
  )
    ? initialData?.countryCode || ""
    : Object.entries(isoCountryMap).find(
        ([, country]) => country === initialData?.countryCode,
      )?.[0] || ""

  const [memberState, setMemberState] = useState(initialCountryCode)
  const [entryType, setEntryType] = useState(initialData?.type || "")
  const [applicationNumber, setApplicationNumber] = useState(initialData?.applicationNumber || "")
  const [applicationDate, setApplicationDate] = useState(toDateInputValue(initialData?.applicationDate))
  const [registrationNumber, setRegistrationNumber] = useState(initialData?.registrationNumber || "")
  const [registrationDate, setRegistrationDate] = useState(toDateInputValue(initialData?.registrationDate))
  const [priorityDate, setPriorityDate] = useState(toDateInputValue(initialData?.priorityDate))
  const [copyFile, setCopyFile] = useState<File | null>(null)
  const [existingDocumentId, setExistingDocumentId] = useState(initialData?.euipoDocumentId || "")
  const [existingFileName, setExistingFileName] = useState(initialData?.originalFileName || "")
  const [errorCopy, setErrorCopy] = useState("")
  const [loading, setLoading] = useState(false)

  const [applicationDateError, setApplicationDateError] = useState("")
  const [registrationDateError, setRegistrationDateError] = useState("")
  const [priorityDateError, setPriorityDateError] = useState("")

  const today = new Date().toISOString().split("T")[0]

  const validateDates = (
    appDate: string,
    regDate: string,
    prioDate: string
  ) => {
    let valid = true

    setApplicationDateError("")
    setRegistrationDateError("")
    setPriorityDateError("")

    if (appDate && appDate > today) {
      setApplicationDateError("Application date cannot be in the future")
      valid = false
    }

    if (regDate && regDate > today) {
      setRegistrationDateError("Registration date cannot be in the future")
      valid = false
    }

    if (appDate && regDate && regDate < appDate) {
      setRegistrationDateError(
        "Registration date cannot be before application date"
      )
      valid = false
    }

    if (prioDate && prioDate > today) {
      setPriorityDateError("Priority date cannot be in the future")
      valid = false
    }

    if (prioDate && appDate && prioDate > appDate) {
      setPriorityDateError(
        "Priority date cannot be after application date"
      )
      valid = false
    }

    return valid
  }

  const handleSubmit = async () => {
    if (!copyFile && !existingDocumentId) return

    setLoading(true)

    try {
      let documentId = existingDocumentId || null
      let originalName = existingFileName || null

      if (copyFile) {
        let fileToUpload = copyFile

        if (copyFile.name.length > 25) {
          const name = copyFile.name
          const extensionIndex = name.lastIndexOf(".")
          const hasExtension = extensionIndex !== -1

          const extension = hasExtension ? name.substring(extensionIndex) : ""
          const baseName = hasExtension
            ? name.substring(0, extensionIndex)
            : name

          const allowedBaseLength = 25 - extension.length

          const truncatedBase =
            allowedBaseLength > 0
              ? baseName.substring(0, allowedBaseLength)
              : baseName.substring(0, 25)

          const newName = truncatedBase + extension

          fileToUpload = new File([copyFile], newName, {
            type: copyFile.type,
          })
        }

        const result = await uploadDocument(fileToUpload, "PRIORITY_COPY")
        documentId = result.euipoDocumentId
        originalName = result.originalFileName
      }

      onSubmit({
        type: entryType,
        countryCode: memberState,
        applicationNumber,
        applicationDate,
        registrationNumber,
        registrationDate,
        priorityDate: priorityDate || undefined,
        euipoDocumentId: documentId,
        originalFileName: originalName,
      })
    } catch {
      setErrorCopy("Document upload failed")
    } finally {
      setLoading(false)
    }
  }

  const isFormValid =
    memberState &&
    entryType &&
    applicationNumber &&
    applicationDate &&
    registrationNumber &&
    registrationDate &&
    (copyFile || existingDocumentId) &&
    !applicationDateError &&
    !registrationDateError &&
    !priorityDateError

  return (
    <div className="mt-3 border-t pt-3 space-y-5 text-sm">
      <div>
        <label className="block mb-1 font-medium">Member state *</label>
        <select
          value={memberState}
          onChange={(e) => setMemberState(e.target.value)}
          className="w-full border rounded p-1.5"
        >
          <option value="">– Select –</option>
          {countries.map((country) => {
            const code = Object.entries(isoCountryMap).find(
              ([, name]) => name === country,
            )?.[0]
            return code ? (
              <option key={code} value={code}>{country}</option>
            ) : null
          })}
        </select>
      </div>

      <div>
        <label className="block mb-1 font-medium">Type of entry *</label>
        <select
          value={entryType}
          onChange={(e) => setEntryType(e.target.value)}
          className="w-full border rounded p-1.5"
        >
          <option value="">– Select –</option>
          <option value="national">National registration</option>
          <option value="international">International registration</option>
        </select>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block mb-1 font-medium">Application number *</label>
          <InfoBox title="Examples of valid formats">
            DE: 39501225<br />
            CZ: A-2019/1234
          </InfoBox>
          <input
            value={applicationNumber}
            onChange={(e) => setApplicationNumber(e.target.value)}
            className="w-full border rounded p-1.5"
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Application date *</label>
          <input
            type="date"
            value={applicationDate}
            onChange={(e) => {
              const v = e.target.value
              setApplicationDate(v)
              validateDates(v, registrationDate, priorityDate)
            }}
            className="w-full border rounded p-1.5"
          />
          {applicationDateError && (
            <p className="text-red-600 text-xs mt-1">
              {applicationDateError}
            </p>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block mb-1 font-medium">
            Registration number *
          </label>
          <input
            value={registrationNumber}
            onChange={(e) => setRegistrationNumber(e.target.value)}
            className="w-full border rounded p-1.5"
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">
            Registration date *
          </label>
          <input
            type="date"
            value={registrationDate}
            onChange={(e) => {
              const v = e.target.value
              setRegistrationDate(v)
              validateDates(applicationDate, v, priorityDate)
            }}
            className="w-full border rounded p-1.5"
          />
          {registrationDateError && (
            <p className="text-red-600 text-xs mt-1">
              {registrationDateError}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block mb-1 font-medium">
          Priority date (optional)
        </label>
        <input
          type="date"
          value={priorityDate}
          onChange={(e) => {
            const v = e.target.value
            setPriorityDate(v)
            validateDates(applicationDate, registrationDate, v)
          }}
          className="w-full border rounded p-1.5"
        />
        {priorityDateError && (
          <p className="text-red-600 text-xs mt-1">
            {priorityDateError}
          </p>
        )}
      </div>

      <div className="border-t pt-3">
        <p className="font-medium mb-2">Copy of earlier entry *</p>
        {existingDocumentId && !copyFile && (
          <div className="mb-2 flex items-center justify-between text-sm text-gray-600">
            <span>Saved copy: {existingFileName || "Uploaded document"}</span>
            <button
              type="button"
              onClick={() => {
                setExistingDocumentId("")
                setExistingFileName("")
              }}
              className="text-red-500 hover:underline"
            >
              Remove
            </button>
          </div>
        )}
        <PdfPreview documentId={existingDocumentId} />
        <FileUploadBox
          uploadedFile={copyFile}
          error={errorCopy}
          onFileSelect={(file) => {
            setCopyFile(file)
            setErrorCopy("")
          }}
          onDelete={() => setCopyFile(null)}
        />
      </div>

      <div className="flex justify-end gap-3 pt-3 border-t">
        <button
          onClick={onCancel}
          className="text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          disabled={!isFormValid || loading}
          onClick={handleSubmit}
          className="px-3 py-1.5 rounded text-sm font-medium bg-blue-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Add seniority
        </button>
      </div>
    </div>
  )
}
